/**
 * Value presentation and per-type validation.
 *
 * Validation messages never include the value. Values can be party PII, and
 * issue lists end up in logs and error responses.
 */
import {
  convertArea, formatArea, formatLocation, isTier3, locationParts, parseArea, validateAadhaar, validatePan, validateTan,
  type AreaUnit, type FieldDef, type FillValue, type PartyType, type Presence,
} from '@saledeed/shared';

export function isPresence(v: FillValue | undefined): v is Presence {
  return typeof v === 'object' && v !== null && v.present === true;
}

/** Undefined, or a string that is only whitespace. Presence is not empty. */
export function isEmpty(v: FillValue | undefined): boolean {
  if (v === undefined) return true;
  if (isPresence(v)) return false;
  return v.trim() === '';
}

/* --------------------------------------------------------------- dates */

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isValidIsoDate(v: string): boolean {
  const m = ISO_DATE.exec(v);
  if (!m) return false;
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1) return false;
  const dim = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  return d <= dim;
}

/** Stored ISO, printed DD-MM-YYYY as Indian deeds do. */
export function formatDate(v: string): string {
  const m = ISO_DATE.exec(v);
  return m && isValidIsoDate(v) ? `${m[3]}-${m[2]}-${m[1]}` : v;
}

/* ------------------------------------------------------------ currency */

const CURRENCY = /^(\d{1,15})(?:\.(\d{1,2}))?$/;

function parseCurrency(v: string): { int: string; paise: string | null } | null {
  const m = CURRENCY.exec(v.replace(/,/g, '').trim());
  if (!m) return null;
  const int = m[1]!.replace(/^0+(?=\d)/, '');
  return { int, paise: m[2] === undefined ? null : m[2].padEnd(2, '0') };
}

/** 150000 -> 1,50,000 ; 150000.5 -> 1,50,000.50 */
export function groupIndian(v: string): string | null {
  const p = parseCurrency(v);
  if (!p) return null;
  const last3 = p.int.slice(-3);
  const rest = p.int.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  const grouped = rest ? `${rest},${last3}` : last3;
  return p.paise === null ? grouped : `${grouped}.${p.paise}`;
}

const ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function below100(n: number): string {
  if (n < 20) return ONES[n]!;
  const t = TENS[Math.floor(n / 10)]!;
  return n % 10 ? `${t} ${ONES[n % 10]!}` : t;
}

function below1000(n: number): string {
  const parts: string[] = [];
  const h = Math.floor(n / 100);
  if (h) parts.push(`${ONES[h]!} Hundred`);
  if (n % 100) parts.push(below100(n % 100));
  return parts.join(' ');
}

/** Indian system: Crore, Lakh, Thousand. */
export function integerInWords(n: number): string {
  if (n === 0) return 'Zero';
  const parts: string[] = [];
  const crore = Math.floor(n / 1e7);
  let r = n % 1e7;
  const lakh = Math.floor(r / 1e5); r %= 1e5;
  const thousand = Math.floor(r / 1e3); r %= 1e3;
  if (crore) parts.push(`${integerInWords(crore)} Crore`);
  if (lakh) parts.push(`${below100(lakh)} Lakh`);
  if (thousand) parts.push(`${below100(thousand)} Thousand`);
  if (r) parts.push(below1000(r));
  return parts.join(' ');
}

/** 150000 -> "One Lakh Fifty Thousand"; 100.50 -> "One Hundred and Fifty Paise". */
export function amountInWords(v: string): string | null {
  const p = parseCurrency(v);
  if (!p) return null;
  const n = Number(p.int);
  if (!Number.isSafeInteger(n)) return null;
  const words = integerInWords(n);
  const paise = p.paise === null ? 0 : Number(p.paise);
  return paise ? `${words} and ${below100(paise)} Paise` : words;
}

/* ---------------------------------------------------------- formatting */

export function formatValue(def: FieldDef, raw: string, format: 'default' | 'words' | AreaUnit): string {
  // Before whitespace is squeezed: a place keeps its English and Telugu names apart with a tab.
  if (def.type === 'location') return formatLocation(raw, def.location?.print).replace(/\s+/g, ' ');
  const v = raw.trim().replace(/\s+/g, ' ');
  switch (def.type) {
    case 'area': {
      const a = parseArea(v);
      if (!a) return v;
      const to = format !== 'default' && format !== 'words' ? format : a.unit;
      return formatArea(convertArea(a.amount, a.unit, to), to);
    }
    case 'calculated': // worked out in context.ts; never typed
    case 'list':
      return v;
    case 'date':
      return formatDate(v);
    case 'currency':
      return (format === 'words' ? amountInWords(v) : groupIndian(v)) ?? v;
    // (area handled above)
    case 'aadhaar': {
      const a = validateAadhaar(v);
      return a.ok ? a.formatted : v;
    }
    case 'pan':
    case 'tan': {
      // Normalise only what looks like a PAN/TAN; anything else prints as entered.
      const compact = v.toUpperCase().replace(/\s/g, '');
      return /^[A-Z]{4,5}\d{4,5}[A-Z]$/.test(compact) ? compact : v;
    }
    case 'text':
    case 'number':
    case 'enum':
      return v;
  }
}

/* ---------------------------------------------------------- validation */

export interface ValueProblem {
  severity: 'error' | 'warning';
  code: string;
  message: string;
}

/**
 * Validates one non-empty string value. Returns problems, never the value.
 * Tier 3 types are only ever checked in the browser.
 */
export function checkValue(def: FieldDef, raw: string, holder: PartyType | 'person' | 'organisation' | null): ValueProblem[] {
  const v = raw.trim();
  const out: ValueProblem[] = [];
  const err = (message: string) => out.push({ severity: 'error', code: 'value.invalid', message });

  if (def.maxLength !== undefined && v.length > def.maxLength) {
    err(`"${def.label}" is longer than ${def.maxLength} characters`);
  }

  switch (def.type) {
    case 'number':
      if (!/^\d+(\.\d+)?$/.test(v)) err(`"${def.label}" must be a number`);
      break;
    case 'currency':
      if (!parseCurrency(v)) err(`"${def.label}" must be an amount like 150000 or 150000.50`);
      break;
    case 'date':
      if (!isValidIsoDate(v)) err(`"${def.label}" must be a valid date`);
      break;
    case 'enum':
      if (!def.options?.includes(v)) err(`"${def.label}" must be one of the listed options`);
      break;
    case 'aadhaar': {
      const a = validateAadhaar(v);
      if (!a.ok) {
        err(a.reason === 'checksum'
          ? `"${def.label}" failed the Aadhaar checksum; re-check each digit against the card`
          : `"${def.label}" is not a valid 12-digit Aadhaar number`);
      }
      break;
    }
    case 'pan': {
      const p = validatePan(v, holder ?? undefined);
      if (!p.ok) err(`"${def.label}" must look like ABCDE1234F`);
      else if (p.partyTypeMismatch) {
        out.push({
          severity: 'warning',
          code: 'pan.partyTypeMismatch',
          message: `"${def.label}" indicates a ${p.holderType.replace(/_/g, ' ')} holder, which does not match the selected party type`,
        });
      }
      break;
    }
    case 'tan':
      if (!validateTan(v).ok) err(`"${def.label}" must look like ABCD12345E`);
      break;
    case 'area': {
      const a = parseArea(v);
      if (!a) err(`"${def.label}" must be an area, e.g. 0.2500 acres`);
      else if (def.units && !def.units.includes(a.unit)) err(`"${def.label}" cannot be given in ${a.unit.replace('_', '. ')}`);
      break;
    }
    case 'calculated':
      err(`"${def.label}" is worked out by the template and cannot be typed`);
      break;
    case 'text':
    case 'list':
      break;
    case 'location':
      // Which places exist is checked in the browser, against the built-in data.
      if (!locationParts(v).en) err(`"${def.label}" must be chosen from the list`);
      break;
  }
  return out;
}

export { isTier3 };
